// feat_351 — PATRÓN VISUAL CANÓNICO de los tabs de /Settings.
//
// Estos son los ladrillos que tienen que usar LOS 8 TABS. La idea es que el
// restyle de cada tab siguiente no vuelva a inventar markup: importa de acá.
//
//     import {
//       TabHeader, SettingsCard, SectionHeader, SectionDivider,
//       ToggleRow, FieldGrid, FieldCell,
//     } from "../components/SettingsPattern";
//
// Las clases viven en `../styles` (el MISMO useStyles compartido de la pantalla),
// no en una hoja JSS propia: todo el look de /Settings se lee de un solo archivo.
//
// 🔴 REGLA DURA HEREDADA DE feat_347 — NADA DE ESTE PATRÓN PUEDE DEPENDER DE
// MONTAJE/DESMONTAJE. Los 8 TabPanel quedan SIEMPRE montados (el shell los oculta
// con `hidden` + display:none) porque 26 campos de texto guardan con `onBlur`.
// Acá no hay Collapse, ni Accordion, ni render condicional por tab activo: todo
// es markup estático. Un acordeón que desmonte su contenido ROMPERÍA el guardado.
//
// Accesibilidad (era CERO antes de feat_351: 0 headings en toda la pantalla):
//   - TabHeader     → <Typography variant="h5"        component="h2">
//   - SectionHeader → <Typography variant="subtitle1" component="h3">
// Los ids settings-tab-* / settings-tabpanel-* con role/aria-controls que agregó
// feat_347 los sigue poniendo el shell (index.js) y no se tocan.

import React from "react";

import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Switch from "@material-ui/core/Switch";
import Tooltip from "@material-ui/core/Tooltip";
import IconButton from "@material-ui/core/IconButton";
import Grid from "@material-ui/core/Grid";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";

import { i18n } from "../../../translate/i18n.js";
import useStyles from "../styles";

// --- 2. Título del tab ------------------------------------------------------
// Uno por tab, arriba de todo. `description` es opcional y va en caption.
export const TabHeader = ({ title, description, id }) => {
	const classes = useStyles();
	return (
		<>
			<Typography variant="h5" component="h2" id={id} className={classes.tabTitle}>
				{title}
			</Typography>
			{description && (
				<Typography variant="caption" color="textSecondary" className={classes.tabTitleDescription}>
					{description}
				</Typography>
			)}
		</>
	);
};

// --- 1. Card ----------------------------------------------------------------
// UNA Paper por SECCIÓN TEMÁTICA. NUNCA una Paper por opción individual (ese es
// el problema que tiene hoy la pantalla: 28 cards sueltas). Un tab de una sola
// sección lleva UNA sola Paper con todo adentro, separado por <SectionDivider>.
export const SettingsCard = ({ children, className, id, ...rest }) => {
	const classes = useStyles();
	return (
		<Paper
			id={id}
			className={className ? `${classes.sectionCard} ${className}` : classes.sectionCard}
			{...rest}
		>
			{children}
		</Paper>
	);
};

// --- 2. Título de sección ---------------------------------------------------
export const SectionHeader = ({ title, description, id }) => {
	const classes = useStyles();
	return (
		<>
			<Typography variant="subtitle1" component="h3" id={id} className={classes.sectionHeading}>
				{title}
			</Typography>
			{description && (
				<Typography variant="caption" color="textSecondary" className={classes.sectionDescription}>
					{description}
				</Typography>
			)}
		</>
	);
};

// Separador ENTRE secciones de una misma Paper. Entre filas de toggle NO hace
// falta: `toggleRow` ya trae su hairline en `& + &`.
export const SectionDivider = () => {
	const classes = useStyles();
	return <Divider className={classes.sectionDivider} />;
};

// --- 3 y 4. Fila de toggle + ayuda larga en tooltip -------------------------
// Estilo WhatsApp: label a la IZQUIERDA, Switch a la DERECHA. NO es el
// FormControlLabel de MUI (que mete el switch adelante del texto).
//
//   [ Label (body1)  (?)                              ] [ Switch ]
//   [ ayuda corta, UNA línea, caption, secundario     ]
//
// `help`    → una línea. Si no entra, se corta con elipsis: el layout no crece.
// `tooltip` → la explicación larga. Se eligió tooltip sobre acordeón porque no
//             mueve el layout al abrirse.
//
// Accesible: `id` va al <input type="checkbox"> real del Switch (MUI v4 lo
// propaga porque type==="checkbox"), el texto es un <label htmlFor> de verdad
// (clickeable), y además se manda aria-label + aria-describedby porque el Switch
// está en OTRA columna del flex y el <label> solo no alcanza para todos los
// lectores. El botón "?" es un IconButton con su propio aria-label.
export const ToggleRow = ({
	id,
	label,
	help,
	tooltip,
	checked,
	onChange,
	disabled,
	ariaLabel,
}) => {
	const classes = useStyles();
	const helpId = help ? `${id}-help` : undefined;
	return (
		<div className={classes.toggleRow}>
			<div className={classes.toggleRowText}>
				<div className={classes.toggleRowLabelLine}>
					<Typography
						variant="body1"
						component="label"
						htmlFor={id}
						className={classes.toggleRowLabel}
					>
						{label}
					</Typography>
					{tooltip && (
						<Tooltip title={tooltip} enterTouchDelay={0} leaveTouchDelay={8000} interactive>
							<IconButton
								size="small"
								className={classes.toggleRowHelpButton}
								aria-label={i18n.t("settingsExtra.moreInfoAriaLabel")}
							>
								<HelpOutlineIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					)}
				</div>
				{help && (
					<Typography
						variant="caption"
						color="textSecondary"
						id={helpId}
						className={classes.toggleRowHelp}
						title={help}
					>
						{help}
					</Typography>
				)}
			</div>
			<Switch
				id={id}
				checked={checked}
				onChange={onChange}
				disabled={disabled}
				color="primary"
				className={classes.toggleRowSwitch}
				inputProps={{
					"aria-label": ariaLabel || label,
					"aria-describedby": helpId,
				}}
			/>
		</div>
	);
};

// --- 5 y 6. Grilla de campos ------------------------------------------------
// 2 columnas para campos CORTOS (minutos, segundos, teléfono, email, números).
// Ancho completo SOLO para URLs, textareas y multiline.
//
//   <FieldGrid>
//     <FieldCell><TextField variant="outlined" size="small" fullWidth …/></FieldCell>
//     <FieldCell><TextField variant="outlined" size="small" fullWidth …/></FieldCell>
//     <FieldCell full><TextField variant="outlined" fullWidth multiline …/></FieldCell>
//   </FieldGrid>
//
// ⚠ Punto 5 del patrón: variant="outlined" SIEMPRE, sin excepción — incluidos los
// <Select native>, que van envueltos en <FormControl variant="outlined">. Hoy la
// pantalla mezcla 31 outlined con 10 standard.
export const FieldGrid = ({ children, ...rest }) => (
	<Grid container spacing={2} {...rest}>
		{children}
	</Grid>
);

export const FieldCell = ({ children, full, ...rest }) => (
	<Grid item xs={12} sm={full ? 12 : 6} {...rest}>
		{children}
	</Grid>
);
